import { Checkbox, Col, Form, message, Modal, notification, Row } from "antd";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import ActionButton from "../../components/action-button";
import InputEmail from "../../components/input-email";
import InputCpf from "@components/input-cpf";
import InputName from "../../components/input-name";
import MiniFormDrawer from "../../components/mini-form-drawer";
import { atualizarSignatario, cadastrarSignatario } from "../../store/assinatura";

import useDispatchWithResult from "../../hooks/useDispatchWithResult";
import { useSelector } from "react-redux";
import { ExclamationCircleOutlined } from "@ant-design/icons";

import { isValidName } from "@util/name";
import { isValidEmail } from "@util/email";
import { validateCPFRule } from "@util/cpf";

const defaultValue = {
  email: "",
  nome: "",
  cpf: "",
  preferencial: false,
};

export default function SignatarioDrawer({ loading, onAfterClose, onChange, onClose, value = defaultValue, visible }) {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loadingSave, setLoadingSave] = useState(false);
  const dispatch = useDispatchWithResult();
  const { signatarios } = useSelector((state) => state.assinaturas);

  const fireChange = () => (typeof onChange === "function" ? onChange() : undefined);
  const fireClose = () => (typeof onClose === "function" ? onClose() : undefined);

  useEffect(() => {
    form.setFieldsValue(value);
  }, [value]);

  const handleDrawerAfterOpen = () => {
    document.querySelector("#nome").focus();
  };

  const checkDuplicateSigner = (signerValues) => {
    const duplicados = [];

    if (signerValues.cpf && signatarios.find((s) => s.cpf === signerValues.cpf && String(s.id) !== String(value.id))) {
      duplicados.push(`CPF: ${signerValues.cpf}`);
    }

    if (signatarios.find((s) => s.email === signerValues.email && String(s.id) !== String(value.id))) {
      duplicados.push(`Email: ${signerValues.email}`);
    }

    if (duplicados.length > 0) {
      Modal.error({
        title: "Dados já cadastrados",
        icon: <ExclamationCircleOutlined style={{ color: "#ff4d4f" }} />,
        content: (
          <div>
            <p>Não é possível salvar, pois já existem registros com:</p>
            <b>{duplicados.join(", ")}</b>
          </div>
        ),
        centered: true,
      });
      return true;
    }

    return false;
  };

  const handleFinish = (values) => {
    if (checkDuplicateSigner(values)) return;

    setLoadingSave(true);
    const signerMapped = {
      email: values.email,
      nome: values.nome,
      cpf: values.cpf ?? "",
      preferencial: values.preferencial ?? false,
    };
    const signerPayload = value.id
      ? {
          signerID: value.id,
          signerData: signerMapped,
        }
      : signerMapped;
    const thunk = value.id ? atualizarSignatario(signerPayload) : cadastrarSignatario(signerPayload);

    dispatch(thunk)
      .then(() => {
        message.success(t(value.id ? "Signatário alterado com sucesso" : "Signatário incluído com sucesso"));
        fireChange();
        fireClose();
      })
      .finally(() => setLoadingSave(false))
      .catch((e) => {
        notification.error({
          message: "Erro ao enviar dados do signatário",
          description: e?.mensagem || "Ocorreu um erro ao enviar os dados do signatário. Tente novamente.",
          duration: 5,
        });
      });
  };

  const footer = (
    <>
      <ActionButton.Save onClick={() => form.submit()} loading={loadingSave} />
      <ActionButton.Close onClick={fireClose} disabled={loadingSave} />
    </>
  );

  const drawerTitle = value.id ? (value.cpf ? `${value.nome} - ${value.cpf}` : value.nome) : t("Criar Signatário");

  return (
    <MiniFormDrawer
      drawerTitle={drawerTitle}
      drawerFooter={footer}
      drawerWidth={500}
      drawerVisible={visible}
      form={form}
      loading={loading}
      onDrawerClose={fireClose}
      onDrawerAfterOpen={handleDrawerAfterOpen}
      onDrawerAfterClose={onAfterClose}
      autoComplete="off"
      onFinish={handleFinish}
    >
      <Form.Item
        name="nome"
        label={t("Nome")}
        rules={[
          { required: true, message: "O nome é obrigatório" },
          {
            validator: (_, value) => (!value || isValidName(value) ? Promise.resolve() : Promise.reject(new Error("Nome inválido"))),
          },
        ]}
      >
        <InputName disabled={loadingSave} />
      </Form.Item>
      <Form.Item
        name="email"
        label={t("E-mail")}
        rules={[
          { required: true, message: "O e-mail é obrigatório" },
          {
            validator: (_, value) => (!value || isValidEmail(value) ? Promise.resolve() : Promise.reject(new Error("E-mail inválido"))),
          },
        ]}
      >
        <InputEmail disabled={loadingSave} />
      </Form.Item>

      {/* <Form.Item name="celular" label={t("Celular")}>
        <InputCelular />
      </Form.Item> */}
      <Form.Item
        name="cpf"
        label={t("CPF")}
        rules={[
          {
            validator: (_, value) => {
              if (!value || value.trim() === "") return Promise.resolve();
              return validateCPFRule(t)(_, value);
            },
          },
        ]}
      >
        <InputCpf disabled={loadingSave} />
      </Form.Item>

      <Row gutter={[16, 0]}>
        <Col flex="auto">
          <Form.Item name="preferencial" valuePropName="checked">
            <Checkbox disabled={loadingSave}>{t("Preferencial")}</Checkbox>
          </Form.Item>
        </Col>
      </Row>
    </MiniFormDrawer>
  );
}

SignatarioDrawer.propTypes = {
  loading: PropTypes.bool,
  onAfterClose: PropTypes.func,
  onChange: PropTypes.func,
  onClose: PropTypes.func,
  value: PropTypes.object,
  visible: PropTypes.bool,
};
